import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Autocomplete,
  Avatar,
  Badge,
  Blockquote,
  Box,
  Breadcrumbs,
  Burger,
  Button,
  Card,
  Checkbox,
  Chip,
  CloseButton,
  Code,
  ColorInput,
  Drawer,
  FileInput,
  Flex,
  Grid,
  Group,
  HoverCard,
  Indicator,
  Kbd,
  List,
  Loader,
  MantineProvider,
  Mark,
  Menu,
  Modal,
  MultiSelect,
  NavLink,
  Notification,
  NumberInput,
  Pagination,
  PasswordInput,
  Popover,
  Progress,
  Radio,
  RangeSlider,
  Rating,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Slider,
  Spoiler,
  Stack,
  Stepper,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { DataTable, DataTableColumn, DataTableSort } from "@dehub/theme";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import {
  ArrowRight,
  Bell,
  Calendar,
  ChartBar,
  ChatCircle,
  CheckCircle,
  CloudArrowUp,
  Code as CodeIcon,
  Cube,
  Envelope,
  Eye,
  Funnel,
  Gear,
  GitBranch,
  Heart,
  House,
  Info,
  Lightning,
  MagnifyingGlass,
  Package,
  PencilSimple,
  Plus,
  Question,
  ShoppingCart,
  Star,
  ArrowsClockwise,
  User,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import { ReactNode, useMemo, useState } from "react";

const sectionGap = "xl" as const;

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Box>
      <Title order={3} mb={4}>
        {children}
      </Title>
      <Box style={{ height: 2, width: 48, background: "var(--mantine-color-brand-6)", borderRadius: 2 }} />
    </Box>
  );
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.6 }}>
      {children}
    </Text>
  );
}

function ShowcaseCard({ children }: { children: ReactNode }) {
  return (
    <Card withBorder radius="md" p="lg">
      {children}
    </Card>
  );
}

/* ─────────────────────────────  Buttons  ───────────────────────────── */

function ButtonShowcase() {
  const variants = ["filled", "light", "outline", "subtle", "default", "transparent"] as const;
  const sizes = ["xs", "sm", "md", "lg"] as const;

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Variants × sizes</SubLabel>
          <Stack gap="sm" mt="xs">
            {variants.map((v) => (
              <Group key={v} gap="sm" wrap="wrap">
                <Text size="sm" w={90} c="dimmed">
                  {v}
                </Text>
                {sizes.map((s) => (
                  <Button key={s} variant={v} size={s} color="brand">
                    Button
                  </Button>
                ))}
              </Group>
            ))}
          </Stack>
        </Box>

        <Box>
          <SubLabel>With icons & loading</SubLabel>
          <Group mt="xs" gap="sm" wrap="wrap">
            <Button leftSection={<House size={16} weight="bold" />}>Дашбоард</Button>
            <Button rightSection={<ArrowRight size={16} weight="bold" />} variant="light">
              Үргэлжлүүлэх
            </Button>
            <Button leftSection={<CloudArrowUp size={16} weight="bold" />} variant="outline">
              Файл байршуулах
            </Button>
            <Button loading>Уншиж байна</Button>
            <Button disabled>Идэвхгүй</Button>
          </Group>
        </Box>

        <Box>
          <SubLabel>Button.Group</SubLabel>
          <Group mt="xs">
            <Button.Group>
              <Button variant="default">Зүүн</Button>
              <Button variant="default">Дунд</Button>
              <Button variant="default">Баруун</Button>
            </Button.Group>
          </Group>
        </Box>

        <Box>
          <SubLabel>ActionIcon variants</SubLabel>
          <Group mt="xs" gap="sm">
            <ActionIcon variant="filled" size="lg">
              <Heart size={18} weight="fill" />
            </ActionIcon>
            <ActionIcon variant="light" size="lg">
              <Heart size={18} weight="fill" />
            </ActionIcon>
            <ActionIcon variant="outline" size="lg">
              <Heart size={18} />
            </ActionIcon>
            <ActionIcon variant="subtle" size="lg">
              <Heart size={18} />
            </ActionIcon>
            <ActionIcon variant="default" size="lg">
              <Heart size={18} />
            </ActionIcon>
            <ActionIcon variant="transparent" size="lg">
              <Heart size={18} />
            </ActionIcon>
          </Group>
        </Box>

        <Box>
          <SubLabel>CloseButton</SubLabel>
          <Group mt="xs" gap="sm">
            <CloseButton size="xs" />
            <CloseButton size="sm" />
            <CloseButton size="md" />
            <CloseButton size="lg" />
          </Group>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────────  Inputs  ───────────────────────────── */

function InputShowcase() {
  const [date, setDate] = useState<string | null>(null);
  return (
    <ShowcaseCard>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        <TextInput label="Нэр" placeholder="Нэрээ оруулна уу" description="Бүрэн нэр" />
        <PasswordInput label="Нууц үг" placeholder="●●●●●●" description="Хамгийн багадаа 8 тэмдэгт" />
        <NumberInput label="Тоо хэмжээ" placeholder="0" min={0} description="Бараа бүтээгдэхүүний тоо" />
        <Textarea label="Тайлбар" placeholder="Тэмдэглэл..." minRows={3} autosize maxRows={4} />
        <FileInput label="Файл" placeholder="Файл сонгох" leftSection={<CloudArrowUp size={16} />} />
        <ColorInput label="Өнгө" defaultValue="#7C3AED" />
        <Select label="Сонголт" placeholder="Сонгох" data={["Active", "Pending", "Disabled"]} />
        <MultiSelect label="Олон сонголт" placeholder="Tags нэмэх" data={["Brand", "Sale", "New", "Hot", "Limited"]} defaultValue={["Brand", "New"]} />
        <Autocomplete label="Хайх" placeholder="Бичиж эхлээрэй" data={["Apple", "Banana", "Cherry", "Date"]} leftSection={<MagnifyingGlass size={16} />} />
        <DatePickerInput
          label="Огноо"
          placeholder="Огноо сонгох"
          leftSection={<Calendar size={16} />}
          value={date}
          onChange={setDate}
        />
        <TextInput label="Алдаатай орлуулах" placeholder="example@dehub.mn" error="И-мэйл буруу байна" defaultValue="bad-email" />
        <TextInput label="Идэвхгүй" placeholder="Засах боломжгүй" disabled defaultValue="Locked" />
      </SimpleGrid>
    </ShowcaseCard>
  );
}

/* ─────────────────────  Selection controls  ───────────────────── */

function SelectionShowcase() {
  const [chips, setChips] = useState<string[]>(["sale"]);
  const [seg, setSeg] = useState("week");
  const [sliderVal, setSliderVal] = useState(40);
  const [rangeVal, setRangeVal] = useState<[number, number]>([20, 75]);
  const [rating, setRating] = useState(3.5);

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Switch</SubLabel>
          <Group mt="xs" gap="lg">
            <Switch label="Идэвхтэй" defaultChecked />
            <Switch label="Имэйл хүлээн авах" defaultChecked color="brand" />
            <Switch label="Темная режим" />
            <Switch label="Disabled" disabled defaultChecked />
          </Group>
        </Box>

        <Box>
          <SubLabel>Checkbox</SubLabel>
          <Group mt="xs" gap="lg">
            <Checkbox label="Шинэ хэрэглэгч" defaultChecked />
            <Checkbox label="Холбогдсон" />
            <Checkbox label="Indeterminate" indeterminate />
            <Checkbox label="Disabled" disabled />
          </Group>
        </Box>

        <Box>
          <SubLabel>Radio.Group</SubLabel>
          <Radio.Group defaultValue="medium" label="Хэмжээ" mt="xs">
            <Group mt="xs">
              <Radio value="small" label="Жижиг" />
              <Radio value="medium" label="Дунд" />
              <Radio value="large" label="Том" />
            </Group>
          </Radio.Group>
        </Box>

        <Box>
          <SubLabel>Chip group (multi)</SubLabel>
          <Chip.Group multiple value={chips} onChange={setChips}>
            <Group mt="xs">
              <Chip value="new">Шинэ</Chip>
              <Chip value="sale">Хямдрал</Chip>
              <Chip value="hot">Hot</Chip>
              <Chip value="limited">Limited</Chip>
            </Group>
          </Chip.Group>
        </Box>

        <Box>
          <SubLabel>SegmentedControl</SubLabel>
          <SegmentedControl
            mt="xs"
            value={seg}
            onChange={setSeg}
            data={[
              { label: "Өдөр", value: "day" },
              { label: "7 хоног", value: "week" },
              { label: "Сар", value: "month" },
              { label: "Жил", value: "year" },
            ]}
          />
        </Box>

        <Box>
          <SubLabel>Slider</SubLabel>
          <Slider mt="xs" value={sliderVal} onChange={setSliderVal} marks={[{ value: 0, label: "0" }, { value: 50, label: "50" }, { value: 100, label: "100" }]} />
        </Box>

        <Box mt="lg">
          <SubLabel>RangeSlider</SubLabel>
          <RangeSlider mt="xs" value={rangeVal} onChange={setRangeVal} />
        </Box>

        <Box mt="lg">
          <SubLabel>Rating</SubLabel>
          <Group mt="xs" gap="lg">
            <Rating value={rating} onChange={setRating} fractions={2} />
            <Rating value={5} readOnly />
          </Group>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Feedback  ───────────────────────── */

function FeedbackShowcase() {
  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Alert</SubLabel>
          <Stack mt="xs" gap="sm">
            <Alert icon={<Info size={18} />} title="Мэдээлэл" color="brand">
              Системд шинэ функц нэмэгдлээ. Шинэчилсэн notes-г үзнэ үү.
            </Alert>
            <Alert icon={<CheckCircle size={18} />} title="Амжилттай" color="green">
              Хадгалалт амжилттай боллоо.
            </Alert>
            <Alert icon={<Warning size={18} />} title="Анхааруулга" color="yellow">
              Stock дуусч байна.
            </Alert>
            <Alert icon={<WarningCircle size={18} />} title="Алдаа" color="red">
              Сүлжээний холболт амжилтгүй.
            </Alert>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Progress</SubLabel>
          <Stack mt="xs" gap="sm">
            <Progress value={28} />
            <Progress value={55} color="green" />
            <Progress value={82} color="yellow" striped animated />
            <Progress.Root size="xl">
              <Progress.Section value={35} color="brand">
                <Progress.Label>35%</Progress.Label>
              </Progress.Section>
              <Progress.Section value={25} color="green">
                <Progress.Label>25%</Progress.Label>
              </Progress.Section>
              <Progress.Section value={20} color="yellow">
                <Progress.Label>20%</Progress.Label>
              </Progress.Section>
            </Progress.Root>
          </Stack>
        </Box>

        <Box>
          <SubLabel>RingProgress</SubLabel>
          <Group mt="xs" gap="lg">
            <RingProgress sections={[{ value: 40, color: "brand" }]} label={<Text ta="center" size="sm" fw={700}>40%</Text>} />
            <RingProgress
              sections={[
                { value: 35, color: "brand" },
                { value: 25, color: "green" },
                { value: 15, color: "yellow" },
              ]}
              label={<Text ta="center" size="sm" fw={700}>75%</Text>}
            />
          </Group>
        </Box>

        <Box>
          <SubLabel>Skeleton</SubLabel>
          <Stack mt="xs" gap="sm" maw={420}>
            <Skeleton height={12} radius="sm" />
            <Skeleton height={12} radius="sm" width="80%" />
            <Skeleton height={12} radius="sm" width="60%" />
            <Group>
              <Skeleton height={40} circle />
              <Stack gap={6} flex={1}>
                <Skeleton height={10} radius="sm" width="40%" />
                <Skeleton height={10} radius="sm" width="70%" />
              </Stack>
            </Group>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Loader</SubLabel>
          <Group mt="xs" gap="lg">
            <Loader type="oval" />
            <Loader type="dots" />
            <Loader type="bars" />
          </Group>
        </Box>

        <Box>
          <SubLabel>Notification (inline)</SubLabel>
          <Stack mt="xs" gap="sm" maw={520}>
            <Notification icon={<CheckCircle size={18} />} color="green" title="Хадгалалт амжилттай">
              Та өөрчлөлтийг амжилттай хадгалсан.
            </Notification>
            <Notification icon={<WarningCircle size={18} />} color="red" title="Алдаа гарлаа" withCloseButton>
              Сервертэй холбогдох боломжгүй байна.
            </Notification>
          </Stack>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Navigation  ───────────────────────── */

function NavigationShowcase() {
  const [active, setActive] = useState(2);
  const [navActive, setNavActive] = useState(1);

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Tabs</SubLabel>
          <Tabs defaultValue="overview" mt="xs">
            <Tabs.List>
              <Tabs.Tab value="overview" leftSection={<ChartBar size={16} />}>
                Тойм
              </Tabs.Tab>
              <Tabs.Tab value="orders" leftSection={<ShoppingCart size={16} />}>
                Захиалга
              </Tabs.Tab>
              <Tabs.Tab value="settings" leftSection={<Gear size={16} />}>
                Тохиргоо
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="overview" pt="md">
              <Text size="sm" c="dimmed">
                Tabs.Panel-н агуулга энд харагдана.
              </Text>
            </Tabs.Panel>
            <Tabs.Panel value="orders" pt="md">
              <Text size="sm" c="dimmed">
                Захиалгын жагсаалт.
              </Text>
            </Tabs.Panel>
            <Tabs.Panel value="settings" pt="md">
              <Text size="sm" c="dimmed">
                Тохиргооны утгууд.
              </Text>
            </Tabs.Panel>
          </Tabs>
        </Box>

        <Box>
          <SubLabel>Stepper</SubLabel>
          <Stepper active={active} onStepClick={setActive} mt="xs">
            <Stepper.Step label="Эхлэх" description="Бүртгэл үүсгэх" />
            <Stepper.Step label="Профайл" description="Мэдээлэл бөглөх" />
            <Stepper.Step label="Баталгаажуулалт" description="Имэйл шалгах" />
            <Stepper.Step label="Тохиргоо" description="Тохируулах" />
            <Stepper.Step label="Дуусгах" description="Бэлэн" />
            <Stepper.Completed>Бүх алхам дууслаа.</Stepper.Completed>
          </Stepper>
        </Box>

        <Box>
          <SubLabel>Pagination</SubLabel>
          <Group mt="xs">
            <Pagination total={10} defaultValue={3} />
          </Group>
        </Box>

        <Box>
          <SubLabel>Accordion</SubLabel>
          <Accordion mt="xs" defaultValue="item-2" variant="separated">
            <Accordion.Item value="item-1">
              <Accordion.Control>Үндсэн тохиргоо</Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">Хэрэглэгчийн үндсэн мэдээлэл, хүсэлтийг тохируулна.</Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="item-2">
              <Accordion.Control>Аюулгүй байдал</Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">Two-factor authentication, нууц үг солих, эрхүүд.</Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="item-3">
              <Accordion.Control>Мэдэгдэл</Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">Имэйл болон Push мэдэгдлийн тохиргоо.</Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>

        <Box>
          <SubLabel>Breadcrumbs</SubLabel>
          <Breadcrumbs mt="xs">
            <Anchor href="#" size="sm">
              Эхлэл
            </Anchor>
            <Anchor href="#" size="sm">
              Tenant
            </Anchor>
            <Text size="sm">Засах</Text>
          </Breadcrumbs>
        </Box>

        <Box>
          <SubLabel>NavLink</SubLabel>
          <Box mt="xs" maw={320} style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: "var(--mantine-radius-md)", overflow: "hidden" }}>
            <NavLink label="Дашбоард" leftSection={<House size={18} />} active={navActive === 0} onClick={() => setNavActive(0)} />
            <NavLink label="Захиалга" leftSection={<ShoppingCart size={18} />} rightSection={<Badge size="sm" color="brand" variant="light">12</Badge>} active={navActive === 1} onClick={() => setNavActive(1)} />
            <NavLink label="Тохиргоо" leftSection={<Gear size={18} />} active={navActive === 2} onClick={() => setNavActive(2)} />
          </Box>
        </Box>

        <Box>
          <SubLabel>Burger</SubLabel>
          <Group mt="xs" gap="sm">
            <Burger />
            <Burger opened />
          </Group>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Data display  ───────────────────────── */

function DataDisplayShowcase() {

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Badge variants</SubLabel>
          <Group mt="xs" gap="sm">
            <Badge variant="filled" color="brand">Filled</Badge>
            <Badge variant="light" color="brand">Light</Badge>
            <Badge variant="outline" color="brand">Outline</Badge>
            <Badge variant="dot" color="brand">Dot</Badge>
            <Badge color="green">Paid</Badge>
            <Badge color="yellow">Pending</Badge>
            <Badge color="red">Failed</Badge>
            <Badge color="blue">Info</Badge>
          </Group>
        </Box>

        <Box>
          <SubLabel>Avatar</SubLabel>
          <Group mt="xs" gap="md" align="center">
            <Avatar color="brand">BB</Avatar>
            <Avatar src="https://i.pravatar.cc/64?img=12" />
            <Avatar color="green" radius="sm">
              <User size={20} />
            </Avatar>
            <Avatar.Group>
              <Avatar color="brand">BB</Avatar>
              <Avatar color="green">SD</Avatar>
              <Avatar color="yellow">EC</Avatar>
              <Avatar>+5</Avatar>
            </Avatar.Group>
            <Indicator color="green" offset={6} size={12} processing>
              <Avatar color="brand" size="lg">
                TG
              </Avatar>
            </Indicator>
          </Group>
        </Box>

        <Box>
          <SubLabel>Card</SubLabel>
          <Group mt="xs" align="stretch" wrap="wrap">
            <Card withBorder radius="md" padding="lg" w={260}>
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Захиалга</Text>
                <Badge color="brand" variant="light">
                  Шинэ
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Card компонент нь бүх контентэн дээр хэрэглэгдэх үндсэн контейнер.
              </Text>
              <Button mt="md" variant="light" fullWidth>
                Дэлгэрэнгүй
              </Button>
            </Card>
          </Group>
        </Box>

        <Box>
          <SubLabel>Code & Kbd</SubLabel>
          <Group mt="xs" gap="sm" align="center">
            <Code>npm install</Code>
            <Code color="var(--mantine-color-brand-light)" c="brand">
              {`const x = 42`}
            </Code>
            <Text size="sm">
              <Kbd>Ctrl</Kbd> + <Kbd>K</Kbd> дарж хайх
            </Text>
          </Group>
        </Box>

        <Box>
          <SubLabel>Timeline</SubLabel>
          <Timeline active={2} bulletSize={24} lineWidth={2} mt="xs">
            <Timeline.Item bullet={<GitBranch size={12} weight="bold" />} title="Бүртгэл үүссэн">
              <Text c="dimmed" size="sm">
                Хэрэглэгч системд бүртгүүллээ
              </Text>
              <Text size="xs" mt={4}>
                2 цаг өмнө
              </Text>
            </Timeline.Item>
            <Timeline.Item bullet={<Envelope size={12} weight="bold" />} title="Имэйл баталгаажсан">
              <Text c="dimmed" size="sm">
                Имэйл хаяг баталгаажуулагдсан
              </Text>
              <Text size="xs" mt={4}>
                1 цаг өмнө
              </Text>
            </Timeline.Item>
            <Timeline.Item bullet={<ShoppingCart size={12} weight="bold" />} title="Эхний захиалга">
              <Text c="dimmed" size="sm">
                Хэрэглэгч анхны захиалгаа өглөө
              </Text>
              <Text size="xs" mt={4}>
                15 минут өмнө
              </Text>
            </Timeline.Item>
            <Timeline.Item bullet={<Package size={12} weight="bold" />} title="Хүргэлт">
              <Text c="dimmed" size="sm">
                Захиалга хүргэгдэж байна
              </Text>
            </Timeline.Item>
          </Timeline>
        </Box>

        <Box>
          <SubLabel>ThemeIcon</SubLabel>
          <Group mt="xs" gap="sm">
            <ThemeIcon variant="filled" size="lg" color="brand">
              <Lightning size={18} weight="fill" />
            </ThemeIcon>
            <ThemeIcon variant="light" size="lg" color="brand">
              <Star size={18} weight="fill" />
            </ThemeIcon>
            <ThemeIcon variant="outline" size="lg" color="brand">
              <Heart size={18} />
            </ThemeIcon>
            <ThemeIcon variant="default" size="lg">
              <Cube size={18} />
            </ThemeIcon>
            <ThemeIcon variant="gradient" gradient={{ from: "brand", to: "cyan" }} size="lg">
              <Bell size={18} weight="fill" />
            </ThemeIcon>
          </Group>
        </Box>

        <Box>
          <SubLabel>Indicator</SubLabel>
          <Group mt="xs" gap="lg">
            <Indicator label="3" size={16} color="red">
              <Button variant="default" leftSection={<Bell size={16} />}>
                Мэдэгдэл
              </Button>
            </Indicator>
            <Indicator processing color="green" size={10}>
              <ThemeIcon variant="light" size="lg">
                <ChatCircle size={18} />
              </ThemeIcon>
            </Indicator>
          </Group>
        </Box>

        <Box>
          <SubLabel>Table</SubLabel>
          <PartnersTableDemo />
        </Box>

        <Box>
          <SubLabel>List</SubLabel>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mt="xs">
            <List size="sm">
              <List.Item>Эхний зүйл</List.Item>
              <List.Item>Хоёрдох зүйл</List.Item>
              <List.Item>Гуравдах зүйл</List.Item>
            </List>
            <List size="sm" type="ordered">
              <List.Item>Бүртгүүлэх</List.Item>
              <List.Item>Баталгаажуулах</List.Item>
              <List.Item>Эхлэх</List.Item>
            </List>
            <List
              size="sm"
              icon={
                <ThemeIcon color="brand" size={18} radius="xl">
                  <CheckCircle size={12} weight="fill" />
                </ThemeIcon>
              }
            >
              <List.Item>Захиалга баталгаажсан</List.Item>
              <List.Item>Төлбөр төлөгдсөн</List.Item>
              <List.Item>Хүргэлт эхэлсэн</List.Item>
            </List>
          </SimpleGrid>
        </Box>

        <Box>
          <SubLabel>Mark / Blockquote / Anchor</SubLabel>
          <Stack mt="xs" gap="sm">
            <Text>
              Энэ нь чухал <Mark color="brand">тэмдэглэгдсэн</Mark> текст юм.
            </Text>
            <Blockquote color="brand" cite="– DeHub Team" icon={<Question size={20} />}>
              "Сайн дизайн нь хэрэглэгчийг айлгадаггүй, харин түүний даалгаврыг хялбарчилдаг."
            </Blockquote>
            <Anchor href="#">Энэ бол anchor линк</Anchor>
          </Stack>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Overlays  ───────────────────────── */

function OverlayShowcase() {
  const [modalOpened, modal] = useDisclosure(false);
  const [drawerOpened, drawer] = useDisclosure(false);

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Modal</SubLabel>
          <Group mt="xs">
            <Button onClick={modal.open}>Modal нээх</Button>
          </Group>
          <Modal opened={modalOpened} onClose={modal.close} title="Modal-н гарчиг" centered>
            <Stack>
              <Text size="sm">
                Энэ бол Modal-н агуулга. Хэрэглэгчээс баталгаажуулалт шаардсан үйлдэл хийхэд ашиглана.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={modal.close}>
                  Болих
                </Button>
                <Button onClick={modal.close}>Үргэлжлүүлэх</Button>
              </Group>
            </Stack>
          </Modal>
        </Box>

        <Box>
          <SubLabel>Drawer</SubLabel>
          <Group mt="xs">
            <Button variant="light" onClick={drawer.open}>
              Drawer нээх
            </Button>
          </Group>
          <Drawer opened={drawerOpened} onClose={drawer.close} title="Drawer-ийн гарчиг" position="right">
            <Stack>
              <Text size="sm">Хажуугаас гарч ирэх panel — filter, дэлгэрэнгүй мэдээллийн хувьд тохиромжтой.</Text>
              <TextInput label="Хайх" placeholder="Бичиж эхлээрэй" />
              <Button onClick={drawer.close}>Хаах</Button>
            </Stack>
          </Drawer>
        </Box>

        <Box>
          <SubLabel>Menu</SubLabel>
          <Group mt="xs">
            <Menu shadow="md" width={220}>
              <Menu.Target>
                <Button variant="default">Menu нээх</Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Үйлдлүүд</Menu.Label>
                <Menu.Item leftSection={<MagnifyingGlass size={14} />} rightSection={<Kbd>⌘K</Kbd>}>
                  Хайх
                </Menu.Item>
                <Menu.Item leftSection={<Envelope size={14} />}>Имэйл илгээх</Menu.Item>
                <Menu.Item leftSection={<Gear size={14} />} rightSection={<Kbd>⌘,</Kbd>}>
                  Тохиргоо
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Бусад</Menu.Label>
                <Menu.Item color="red" leftSection={<WarningCircle size={14} />}>
                  Устгах
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Box>

        <Box>
          <SubLabel>Tooltip</SubLabel>
          <Group mt="xs">
            <Tooltip label="Энэ бол tooltip">
              <Button variant="light">Hover хийх</Button>
            </Tooltip>
            <Tooltip label="Доороос харагдана" position="bottom" color="brand">
              <ActionIcon variant="filled" size="lg">
                <Info size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Box>

        <Box>
          <SubLabel>Popover</SubLabel>
          <Group mt="xs">
            <Popover width={260} position="bottom" withArrow shadow="md">
              <Popover.Target>
                <Button variant="default">Popover нээх</Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap="xs">
                  <Text size="sm" fw={600}>
                    Шуурхай тохиргоо
                  </Text>
                  <Text size="xs" c="dimmed">
                    Popover нь жижиг form, action menu хэрэгтэй үед сайн.
                  </Text>
                  <TextInput placeholder="Утга" size="xs" />
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>
        </Box>

        <Box>
          <SubLabel>HoverCard</SubLabel>
          <Group mt="xs">
            <HoverCard width={280} shadow="md">
              <HoverCard.Target>
                <Anchor>@dehub</Anchor>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Group>
                  <Avatar color="brand">DH</Avatar>
                  <Box>
                    <Text fw={600} size="sm">
                      DeHub
                    </Text>
                    <Text size="xs" c="dimmed">
                      B2B Commerce Platform
                    </Text>
                  </Box>
                </Group>
              </HoverCard.Dropdown>
            </HoverCard>
          </Group>
        </Box>

        <Box>
          <SubLabel>Spoiler</SubLabel>
          <Spoiler maxHeight={48} showLabel="Дэлгэрэнгүй харах" hideLabel="Хураах" mt="xs">
            <Text size="sm">
              Энэ бол их урт текст юм. Spoiler нь өндрийг хязгаарлаж, "Дэлгэрэнгүй харах" товчлуурыг харуулна.
              Хэрэглэгч товчийг даран бүх агуулгыг үзэх боломжтой. Урт тайлбар, terms-of-service, дэлгэрэнгүй
              мэдээллүүдэд хэрэглэхэд тохиромжтой. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam.
            </Text>
          </Spoiler>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Typography  ───────────────────────── */

function TypographyShowcase() {
  return (
    <ShowcaseCard>
      <Stack gap="md">
        <Box>
          <SubLabel>Title (order 1–6)</SubLabel>
          <Stack mt="xs" gap={4}>
            <Title order={1}>Heading 1 — гарчиг</Title>
            <Title order={2}>Heading 2 — гарчиг</Title>
            <Title order={3}>Heading 3 — гарчиг</Title>
            <Title order={4}>Heading 4 — гарчиг</Title>
            <Title order={5}>Heading 5 — гарчиг</Title>
            <Title order={6}>Heading 6 — гарчиг</Title>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Text sizes</SubLabel>
          <Stack mt="xs" gap={2}>
            <Text size="xs">size=xs — жижигхэн текст</Text>
            <Text size="sm">size=sm — жижиг текст</Text>
            <Text size="md">size=md — энгийн текст</Text>
            <Text size="lg">size=lg — том текст</Text>
            <Text size="xl">size=xl — маш том текст</Text>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Weights & colors</SubLabel>
          <Stack mt="xs" gap={2}>
            <Text fw={400}>fw=400 normal</Text>
            <Text fw={500}>fw=500 medium</Text>
            <Text fw={600}>fw=600 semibold</Text>
            <Text fw={700}>fw=700 bold</Text>
            <Text c="brand" fw={600}>
              c="brand"
            </Text>
            <Text c="dimmed">c="dimmed"</Text>
            <Text c="red" fw={500}>
              c="red"
            </Text>
            <Text c="green" fw={500}>
              c="green"
            </Text>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Inline elements</SubLabel>
          <Text mt="xs">
            Энд <Mark color="brand">highlight</Mark> хийсэн текст, <Code>inline code</Code>,{" "}
            <Text span fw={700}>
              bold span
            </Text>{" "}
            болон <Anchor href="#">link</Anchor> орсон.
          </Text>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Layout  ───────────────────────── */

function LayoutShowcase() {
  const boxStyle: React.CSSProperties = {
    background: "var(--mantine-color-brand-light)",
    color: "var(--mantine-color-brand-light-color)",
    borderRadius: "var(--mantine-radius-sm)",
    padding: "var(--mantine-spacing-sm)",
    textAlign: "center",
    fontSize: "var(--mantine-font-size-sm)",
  };

  return (
    <ShowcaseCard>
      <Stack gap="lg">
        <Box>
          <SubLabel>Group (horizontal)</SubLabel>
          <Group mt="xs" gap="sm">
            <Box style={boxStyle}>1</Box>
            <Box style={boxStyle}>2</Box>
            <Box style={boxStyle}>3</Box>
            <Box style={boxStyle}>4</Box>
          </Group>
        </Box>

        <Box>
          <SubLabel>Stack (vertical)</SubLabel>
          <Stack mt="xs" gap="sm" maw={240}>
            <Box style={boxStyle}>Эхний</Box>
            <Box style={boxStyle}>Хоёрдох</Box>
            <Box style={boxStyle}>Гурав дахь</Box>
          </Stack>
        </Box>

        <Box>
          <SubLabel>Flex with justify</SubLabel>
          <Flex mt="xs" gap="sm" justify="space-between" style={{ background: "var(--mantine-color-gray-1)", padding: "var(--mantine-spacing-sm)", borderRadius: "var(--mantine-radius-sm)" }}>
            <Box style={boxStyle}>Зүүн</Box>
            <Box style={boxStyle}>Дунд</Box>
            <Box style={boxStyle}>Баруун</Box>
          </Flex>
        </Box>

        <Box>
          <SubLabel>Grid (responsive)</SubLabel>
          <Grid mt="xs">
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Box style={boxStyle}>md=3</Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Box style={boxStyle}>md=3</Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Box style={boxStyle}>md=3</Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Box style={boxStyle}>md=3</Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Box style={boxStyle}>md=8</Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Box style={boxStyle}>md=4</Box>
            </Grid.Col>
          </Grid>
        </Box>

        <Box>
          <SubLabel>SimpleGrid</SubLabel>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mt="xs">
            <Box style={boxStyle}>1</Box>
            <Box style={boxStyle}>2</Box>
            <Box style={boxStyle}>3</Box>
            <Box style={boxStyle}>4</Box>
          </SimpleGrid>
        </Box>

        <Box>
          <SubLabel>ScrollArea (constrained)</SubLabel>
          <ScrollArea h={140} mt="xs" style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: "var(--mantine-radius-sm)" }} p="sm">
            <Stack gap="xs">
              {Array.from({ length: 24 }).map((_, i) => (
                <Group key={i} justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon variant="light" size="sm">
                      <CodeIcon size={12} />
                    </ThemeIcon>
                    <Text size="sm">Жагсаалтын мөр #{i + 1}</Text>
                  </Group>
                  <Badge size="sm" variant="light">
                    {i % 3 === 0 ? "new" : "ok"}
                  </Badge>
                </Group>
              ))}
            </Stack>
          </ScrollArea>
        </Box>
      </Stack>
    </ShowcaseCard>
  );
}

/* ─────────────────────────  Root  ───────────────────────── */

export function Sketchbook() {
  const [variant, setVariant] = useState<"flat" | "modern">("flat");
  const baseTheme = useMantineTheme();
  const variantTheme = useMemo(() => (variant === "modern" ? buildModernTheme(baseTheme) : baseTheme), [variant, baseTheme]);

  return (
    <Stack gap={sectionGap}>
      <Box>
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <Box>
            <Title order={2}>UI Sketchbook</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Tenant-ийн идэвхтэй theme дээр Mantine v9 component library бүхэлд нь харуулна. Үндсэн өнгө,
              token-уудыг өөрчлөхөд бүх элемент шууд шинэчлэгдэнэ.
            </Text>
          </Box>
          <SegmentedControl
            value={variant}
            onChange={(v) => setVariant(v as "flat" | "modern")}
            data={[
              { label: "Flat", value: "flat" },
              { label: "Modern", value: "modern" },
            ]}
          />
        </Group>
        <Text size="xs" c="dimmed" mt="sm">
          {variant === "flat"
            ? "Mantine v9 анхдагч төрх — flat, минимал."
            : "Илүү гүн сүүдэр, бөөрөнхий булан, gradient — орчин үеийн загвартай."}
        </Text>
      </Box>

      <MantineProvider theme={variantTheme}>

      <Box>
        <SectionTitle>Buttons</SectionTitle>
        <Box mt="md">
          <ButtonShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Inputs</SectionTitle>
        <Box mt="md">
          <InputShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Selection controls</SectionTitle>
        <Box mt="md">
          <SelectionShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Feedback</SectionTitle>
        <Box mt="md">
          <FeedbackShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Navigation</SectionTitle>
        <Box mt="md">
          <NavigationShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Data display</SectionTitle>
        <Box mt="md">
          <DataDisplayShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Overlays</SectionTitle>
        <Box mt="md">
          <OverlayShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Typography</SectionTitle>
        <Box mt="md">
          <TypographyShowcase />
        </Box>
      </Box>

      <Box>
        <SectionTitle>Layout</SectionTitle>
        <Box mt="md">
          <LayoutShowcase />
        </Box>
      </Box>
      </MantineProvider>
    </Stack>
  );
}

/* ─────────────────────────  Modern theme overrides  ───────────────────────── */

function buildModernTheme(base: ReturnType<typeof useMantineTheme>) {
  // Brand-tinted layered shadows + colored glow for filled surfaces.
  const softShadow = "0 1px 2px rgba(15,23,41,0.04), 0 8px 20px -6px rgba(15,23,41,0.10), 0 16px 40px -12px rgba(15,23,41,0.10)";
  const lifted = "0 4px 12px -2px rgba(15,23,41,0.10), 0 18px 36px -10px rgba(15,23,41,0.14)";
  const dramatic = "0 12px 32px -8px rgba(15,23,41,0.20), 0 32px 64px -16px rgba(15,23,41,0.16)";
  const brandGlow = "0 6px 18px -6px color-mix(in oklch, var(--mantine-color-brand-6) 70%, transparent), 0 12px 28px -8px color-mix(in oklch, var(--mantine-color-brand-6) 40%, transparent)";

  return {
    ...base,
    defaultRadius: "lg" as const,
    cursorType: "pointer" as const,
    focusRing: "auto" as const,
    shadows: {
      ...base.shadows,
      xs: "0 1px 2px rgba(15,23,41,0.05)",
      sm: softShadow,
      md: lifted,
      lg: dramatic,
      xl: dramatic,
    },
    components: {
      ...(base.components || {}),
      Button: {
        defaultProps: { radius: "xl" },
        styles: () => ({
          root: {
            transition: "transform 200ms cubic-bezier(0.2,0,0,1), box-shadow 200ms ease, filter 200ms ease",
            fontWeight: 600,
            letterSpacing: "0.01em",
          },
        }),
        vars: (_t: unknown, props: { variant?: string; color?: string }) => {
          if (props.variant === "filled") {
            return {
              root: {
                "--button-bg":
                  "linear-gradient(135deg, var(--mantine-color-brand-7) 0%, var(--mantine-color-brand-5) 100%)",
                "--button-hover":
                  "linear-gradient(135deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)",
              },
            };
          }
          return { root: {} };
        },
      },
      ActionIcon: {
        defaultProps: { radius: "lg" },
        styles: () => ({
          root: {
            transition: "transform 160ms cubic-bezier(0.2,0,0,1), box-shadow 160ms ease, background 160ms ease",
          },
        }),
      },
      Card: {
        defaultProps: { radius: "xl", shadow: "sm", withBorder: false, padding: "lg" },
        styles: () => ({
          root: {
            backgroundImage:
              "linear-gradient(180deg, color-mix(in oklch, var(--mantine-color-white) 100%, transparent) 0%, color-mix(in oklch, var(--mantine-color-gray-0) 50%, transparent) 100%)",
            transition: "transform 220ms cubic-bezier(0.2,0,0,1), box-shadow 220ms ease",
          },
        }),
      },
      Paper: {
        defaultProps: { radius: "lg", shadow: "sm" },
      },
      TextInput: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
        styles: () => ({
          input: {
            transition: "background 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
          },
        }),
      },
      PasswordInput: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
      },
      NumberInput: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
      },
      Textarea: {
        defaultProps: { radius: "md", variant: "filled" },
      },
      Select: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
      },
      MultiSelect: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
      },
      Autocomplete: {
        defaultProps: { radius: "md", size: "md", variant: "filled" },
      },
      ColorInput: {
        defaultProps: { radius: "md", variant: "filled" },
      },
      FileInput: {
        defaultProps: { radius: "md", variant: "filled" },
      },
      Badge: {
        defaultProps: { radius: "md" },
        styles: (_t: unknown, props: { variant?: string }) => {
          if (props?.variant === "filled") {
            return {
              root: {
                backgroundImage:
                  "linear-gradient(135deg, var(--mantine-color-brand-7), var(--mantine-color-brand-5))",
                boxShadow: brandGlow,
              },
            };
          }
          return { root: {} };
        },
      },
      Tabs: {
        defaultProps: { variant: "pills", radius: "lg" },
        styles: () => ({
          tab: {
            transition: "background 160ms ease, color 160ms ease, box-shadow 160ms ease",
            fontWeight: 500,
          },
        }),
      },
      Modal: {
        defaultProps: {
          radius: "xl",
          shadow: "xl",
          overlayProps: { backgroundOpacity: 0.5, blur: 12 },
          transitionProps: { duration: 220, transition: "pop" },
        },
        styles: () => ({
          content: { backdropFilter: "saturate(140%)" },
          header: { paddingBottom: "var(--mantine-spacing-xs)" },
        }),
      },
      Drawer: {
        defaultProps: { radius: "xl", shadow: "xl", overlayProps: { backgroundOpacity: 0.5, blur: 12 } },
      },
      Menu: {
        defaultProps: { shadow: "md", radius: "lg" },
        styles: () => ({
          dropdown: { padding: 6 },
          item: { borderRadius: 8, fontWeight: 500 },
        }),
      },
      Popover: {
        defaultProps: { radius: "lg", shadow: "md" },
      },
      HoverCard: {
        defaultProps: { radius: "lg", shadow: "md" },
      },
      Notification: {
        defaultProps: { radius: "lg" },
        styles: () => ({
          root: { boxShadow: lifted },
        }),
      },
      Tooltip: {
        defaultProps: { radius: "md", withArrow: true, openDelay: 200 },
      },
      SegmentedControl: {
        defaultProps: { radius: "xl" },
        styles: () => ({
          root: {
            backgroundImage:
              "linear-gradient(180deg, color-mix(in oklch, var(--mantine-color-gray-1) 100%, transparent), color-mix(in oklch, var(--mantine-color-gray-0) 100%, transparent))",
          },
          indicator: { boxShadow: softShadow },
        }),
      },
      Switch: {
        defaultProps: { size: "md" },
        styles: () => ({
          track: { boxShadow: "inset 0 1px 2px rgba(15,23,41,0.06)" },
          thumb: { boxShadow: "0 2px 4px rgba(15,23,41,0.12)" },
        }),
      },
      Slider: {
        defaultProps: { size: "md" },
        styles: () => ({
          thumb: { boxShadow: brandGlow, transition: "transform 160ms ease" },
          bar: { backgroundImage: "linear-gradient(90deg, var(--mantine-color-brand-5), var(--mantine-color-brand-6))" },
        }),
      },
      Avatar: {
        defaultProps: { radius: "xl" },
      },
      Chip: {
        defaultProps: { radius: "xl" },
      },
      Pagination: {
        defaultProps: { radius: "md" },
      },
      Stepper: {
        defaultProps: { radius: "md" },
      },
      Alert: {
        defaultProps: { radius: "lg" },
      },
      Progress: {
        defaultProps: { radius: "xl" },
        styles: () => ({
          section: {
            backgroundImage:
              "linear-gradient(90deg, var(--mantine-color-brand-5), var(--mantine-color-brand-6))",
          },
        }),
      },
      RingProgress: {
        styles: () => ({
          root: { filter: "drop-shadow(0 4px 8px rgba(15,23,41,0.06))" },
        }),
      },
      Indicator: {
        defaultProps: { withBorder: true },
      },
      ThemeIcon: {
        defaultProps: { radius: "lg" },
      },
      Accordion: {
        defaultProps: { radius: "lg", variant: "separated" },
      },
      Loader: {
        defaultProps: { type: "dots" },
      },
    },
  };
}

/* ─────────────────────────  Partners Table  ───────────────────────── */

interface Partner {
  name: string;
  type: string;
  sector: string;
  contact: string;
  volume: string;
  rating: number;
  status: "active" | "pending" | "paused";
  since: string;
}

const PARTNERS: Partner[] = [
  { name: "Tavan Bogd Group", type: "Distributor", sector: "FMCG", status: "active", contact: "M. Эрдэнэ", volume: "₮ 2.4B", since: "2021-04", rating: 4.8 },
  { name: "MCS Coca-Cola", type: "Manufacturer", sector: "Beverage", status: "active", contact: "B. Сарнай", volume: "₮ 8.1B", since: "2019-09", rating: 4.9 },
  { name: "Khan Brewery", type: "Manufacturer", sector: "Beverage", status: "active", contact: "Ц. Гантулга", volume: "₮ 3.7B", since: "2020-02", rating: 4.6 },
  { name: "Asia Pharma", type: "Distributor", sector: "Healthcare", status: "pending", contact: "Д. Наран", volume: "₮ 940M", since: "2024-01", rating: 4.2 },
  { name: "Mongol Daatgal", type: "Service", sector: "Insurance", status: "paused", contact: "О. Болор", volume: "—", since: "2022-06", rating: 4.0 },
  { name: "Gobi Cashmere", type: "Manufacturer", sector: "Apparel", status: "active", contact: "А. Туяа", volume: "₮ 1.2B", since: "2018-11", rating: 4.7 },
  { name: "Nomin Holding", type: "Distributor", sector: "Retail", status: "active", contact: "Б. Энхболд", volume: "₮ 5.6B", since: "2017-03", rating: 4.5 },
  { name: "Tugrug Mining", type: "Manufacturer", sector: "Mining", status: "pending", contact: "Г. Мөнхбат", volume: "₮ 720M", since: "2024-09", rating: 3.9 },
];

function statusBadge(s: Partner["status"]) {
  if (s === "active") return <Badge color="green" variant="light" size="sm">Идэвхтэй</Badge>;
  if (s === "pending") return <Badge color="yellow" variant="light" size="sm">Хүлээгдэж</Badge>;
  return <Badge color="gray" variant="light" size="sm">Зогсоосон</Badge>;
}

function PartnersTableDemo() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<DataTableSort>({ key: "name", direction: "asc" });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const filtered = search
    ? PARTNERS.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || p.contact.toLowerCase().includes(search.toLowerCase()))
    : PARTNERS;

  const sorted = [...filtered].sort((a, b) => {
    const k = sort.key as keyof Partner;
    let av: string | number = a[k] as any;
    let bv: string | number = b[k] as any;
    if (k === "volume") {
      const parse = (v: string) => {
        if (v === "—") return 0;
        const num = parseFloat(v.replace(/[^0-9.]/g, ""));
        return v.includes("B") ? num * 1000 : num;
      };
      av = parse(a.volume);
      bv = parse(b.volume);
    }
    if (av < bv) return sort.direction === "asc" ? -1 : 1;
    if (av > bv) return sort.direction === "asc" ? 1 : -1;
    return 0;
  });

  const columns: DataTableColumn<Partner>[] = [
    {
      key: "name",
      header: "Партнер",
      sortable: true,
      render: (p) => (
        <Group gap="sm" wrap="nowrap">
          <Avatar name={p.name} size="sm" radius="xl" color="initials" />
          <Box>
            <Text size="sm" fw={600}>{p.name}</Text>
            <Text size="xs" c="dimmed">Гэрээ: {p.since}</Text>
          </Box>
        </Group>
      ),
    },
    { key: "type", header: "Төрөл", sortable: true, render: (p) => <Text size="sm" c="dimmed">{p.type}</Text> },
    { key: "sector", header: "Сектор", render: (p) => <Text size="sm" c="dimmed">{p.sector}</Text> },
    { key: "contact", header: "Холбогдох", render: (p) => <Text size="sm" c="dimmed">{p.contact}</Text> },
    {
      key: "volume",
      header: "Эргэлт",
      sortable: true,
      align: "right",
      render: (p) => <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{p.volume}</Text>,
    },
    {
      key: "rating",
      header: "Үнэлгээ",
      sortable: true,
      render: (p) => (
        <Group gap={4} wrap="nowrap">
          <Star size={13} weight="fill" color="var(--mantine-color-yellow-5)" />
          <Text size="sm" fw={600}>{p.rating.toFixed(1)}</Text>
        </Group>
      ),
    },
    { key: "status", header: "Төлөв", render: (p) => statusBadge(p.status) },
  ];

  const toolbar = (
    <Group justify="space-between" p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
      <Box>
        <Title order={5}>Партнерүүд</Title>
        <Text size="xs" c="dimmed" mt={2}>
          {selected.size > 0
            ? `${selected.size} сонгогдсон`
            : `${PARTNERS.length} бүртгэлтэй · ${PARTNERS.filter((p) => p.status === "active").length} идэвхтэй`}
        </Text>
      </Box>
      <Group gap="xs">
        {selected.size > 0 ? (
          <>
            <Button size="xs" variant="default">Экспорт</Button>
            <Button size="xs" color="red" variant="light">Зогсоох</Button>
          </>
        ) : (
          <>
            <TextInput size="xs" placeholder="Хайх..." value={search} onChange={(e) => setSearch(e.currentTarget.value)} leftSection={<MagnifyingGlass size={13} />} w={200} />
            <Button size="xs" variant="default" leftSection={<Funnel size={14} />}>Шүүх</Button>
            <ActionIcon size="lg" variant="default" aria-label="Refresh">
              <ArrowsClockwise size={15} />
            </ActionIcon>
            <Button size="xs" leftSection={<Plus size={14} weight="bold" />}>Шинэ партнер</Button>
          </>
        )}
      </Group>
    </Group>
  );

  return (
    <DataTable<Partner, string>
      data={sorted}
      rowKey={(p) => p.name}
      columns={columns}
      actions={() => [
        { icon: Eye, label: "Үзэх" },
        { icon: PencilSimple, label: "Засах" },
        { icon: Cube, label: "Захиалгууд" },
        { icon: ChartBar, label: "Статистик" },
        { icon: Warning, label: "Зогсоох", color: "red" },
      ]}
      selection={{ value: selected, onChange: setSelected }}
      sort={sort}
      onSortChange={setSort}
      toolbar={toolbar}
      pagination={{
        page,
        total: 12,
        onChange: setPage,
        label: `1–${sorted.length} / ${PARTNERS.length} бичлэг`,
      }}
    />
  );
}
